import { CClient, showErrorAlert } from '@common/utils'
import SelectInput from '@components/common/forms/select'
import Modal from '@components/common/modal'
import Spinner from '@components/common/spinner'
import * as Sentry from '@sentry/node'
import classNames from 'classnames'
import tc from 'countries-and-timezones'
import date from '@lib/utils/date.utils'
import { useMemo, useState } from 'react'
import Router from 'next/router'
import cogoToast from 'cogo-toast'
import { X } from 'react-feather'
import { t, Trans } from '@lingui/macro'

export type TimezoneModalProps = {
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  selectedTimezone: string
  setSelectedTimezone: React.Dispatch<React.SetStateAction<string>>
  isReload?: boolean
  // When false, the selection is kept local (not saved to the user profile).
  persist?: boolean
}

const TimezoneModal = ({
  isOpen,
  setIsOpen,
  selectedTimezone,
  setSelectedTimezone,
  isReload = false,
  persist = true,
}: TimezoneModalProps) => {
  const [currentSelectedTimezone, setCurrentSelectedTimezone] = useState(
    selectedTimezone
  )
  const [isLoading, setIsLoading] = useState(false)

  const handleChangeTimezone = async () => {
    if (!persist) {
      // Local-only: caller persists later (e.g. on confirm).
      setSelectedTimezone(currentSelectedTimezone)
      setIsOpen(false)
      return
    }
    setIsLoading(true)
    try {
      const response = await CClient('/api/v1/users/me/', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ timezone: currentSelectedTimezone }),
      })
      if (response.ok) {
        if (isReload) {
          cogoToast
            .success(
              t({
                id: 'trialClass.booking.timezoneModal.successMessage',
                message: `Timezone has been updated successfully.`,
              })
            )
            .then(() => Router.reload())
        }
      } else throw new Error(response.statusText)
    } catch (error) {
      Sentry.captureException(error)
      showErrorAlert({
        text: t({
          id: 'trialClass.booking.timezoneModal.errorMessage',
          message: `Something went wrong while updating your timezone preferences.`,
        }),
      })
    }
    setIsLoading(false)
    setSelectedTimezone(currentSelectedTimezone)
    setIsOpen(false)
  }

  const timezoneOptions = useMemo(
    () =>
      Object.values(tc.getAllTimezones())
        .sort(
          (timezoneA, timezoneB) => timezoneA.dstOffset - timezoneB.dstOffset
        )
        .map((timezone) => ({
          label: `(GMT ${timezone.dstOffsetStr}) ${timezone.name}`,
          value: timezone.name,
        })),
    []
  )

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
      <>
        <div className="flex justify-end">
          <X
            className="cursor-pointer w-5 text-grey-800"
            onClick={() => setIsOpen(false)}
          />
        </div>
        <div className="flex flex-col mx-5 mb-5 lg:mx-0 lg:mb-0 space-y-2">
          <p className="text-2xl font-600">
            <Trans id="trialClass.booking.timezoneModal.heading">
              Update your timezone
            </Trans>
          </p>
          <p className="leading-tight text-grey">
            <Trans id="trialClass.booking.timezoneModal.subHeading">
              Your current timezone is {selectedTimezone} (GMT{' '}
              {date().tz(selectedTimezone).format('Z')}). You can change it by
              selecting new timezone from below
            </Trans>
          </p>
          <SelectInput
            options={timezoneOptions}
            id="timezone-dropdown"
            handleSelectChange={(id, obj) =>
              setCurrentSelectedTimezone(obj.value)
            }
            value={timezoneOptions.find(
              (timezoneOption) =>
                timezoneOption.value === currentSelectedTimezone
            )}
            required
            isInModal
          />
          <div className="flex pt-2 space-x-5">
            <button
              className={classNames('btn btn-orange', {
                'cursor-not-allowed': isLoading,
              })}
              disabled={isLoading}
              onClick={handleChangeTimezone}
            >
              {isLoading ? (
                <Spinner classes="w-5" />
              ) : (
                <Trans id="trialClass.booking.timezoneModal.ctaText">
                  Update
                </Trans>
              )}
            </button>
            <button
              className={classNames('text-orange font-600 hover:underline', {
                'cursor-not-allowed': isLoading,
              })}
              disabled={isLoading}
              onClick={() => setIsOpen(false)}
            >
              <Trans id="trialClass.booking.timezoneModal.cancelBtnText">
                Cancel
              </Trans>
            </button>
          </div>
        </div>
      </>
    </Modal>
  )
}

export default TimezoneModal
